import { LayoutSizeType, SHAPES, TYPES } from '../../render/constants';
import { generateKeyBetween } from 'fractional-indexing';
import { createId } from '../../render/utils/node';
import { applyTransformToPath, resolveNormalizedPath, resolvePathToBox } from './path';
import {
  processImages,
  getStyleValue,
  getElementTransform,
  resolveFontFamily,
  resolveFontSize,
  isSimpleLine,
  matrixToString,
  isImageFilled,
  multiplyMatrix,
  getStyles,
  detectArrowMarker,
  resolveLineStrokeDasharray,
  extractLineCoords,
  processGradients,
  processPatterns,
  processFilters,
} from './svg-utils';
import {
  resolveFill,
  resolveStroke,
  resolveStrokeWidth,
  resolveFilter,
  resolveOpacity,
  resolveRadius,
  resolveStrokeOpacity,
  resolveStrokeLinecap,
  resolveStrokeLinejoin,
  resolveStrokeMiterlimit,
  resolveStrokeDasharray,
  resolveStrokeDashoffset,
  resolveFillRule,
  resolveFontWeight,
  resolveFontStyle,
  resolveTextDecoration,
  resolveTextAnchor,
} from './svg-resolve';

export function svg2Node(svgContent, size) {
  // Create a temporary container to parse SVG
  const parser = new DOMParser();
  const svgDoc = parser.parseFromString(svgContent, 'image/svg+xml');

  // Create an SVG element in the DOM to get accurate bounding box measurements
  const tempEl = document.createElement('div');
  tempEl.style.position = 'absolute';
  tempEl.style.top = '0px';
  tempEl.style.left = '0px';
  tempEl.style.pointerEvents = 'none';
  tempEl.style.visibility = 'hidden';
  document.body.appendChild(tempEl);
  tempEl.innerHTML = svgContent || '';
  let tempSvg =
    tempEl.querySelector('svg') || (svgDoc && svgDoc.querySelector && svgDoc.querySelector('svg'));
  if (!tempSvg) {
    // If content isn't wrapped in <svg>, wrap it to ensure a valid element
    const wrapped = `<svg xmlns="http://www.w3.org/2000/svg">${svgContent || ''}</svg>`;
    tempEl.innerHTML = wrapped;
    tempSvg = tempEl.querySelector('svg');
  }
  if (!tempSvg) {
    document.body.removeChild(tempEl);
    throw new Error('No <svg> element found in provided content.');
  }
  const svgElement = tempSvg;
  // Set SVG dimensions based on viewBox if present
  const viewBoxV = tempSvg.getAttribute('viewBox');
  size = size || {
    w: svgElement.width,
    h: svgElement.height,
  };
  if (viewBoxV) {
    const [, , width, height] = viewBoxV.split(' ').map(Number);
    tempSvg.style.width = `${width}px`;
    tempSvg.style.height = `${height}px`;
  } else {
    // Set default size if no viewBoxV
    tempSvg.style.width = size.w ? `${size.w}px` : '800px';
    tempSvg.style.height = size.h ? `${size.h}px` : '600px';
  }

  // Handle parsing errors
  const parserError = svgDoc.querySelector('parsererror');
  if (parserError) {
    throw new Error('SVG parsing error: ' + parserError.textContent);
  }

  // Get SVG dimensions
  let width = svgElement.getAttribute('width');
  let height = svgElement.getAttribute('height');
  const viewBox = svgElement.getAttribute('viewBox');

  // Parse dimensions
  if (viewBox && (!width || !height)) {
    const viewBoxValues = viewBox.split(/\s+/).map(Number);
    if (viewBoxValues.length === 4) {
      width = width || viewBoxValues[2];
      height = height || viewBoxValues[3];
    }
  }

  // Convert to numbers and apply default size if needed
  width = parseFloat(width) || size.w || 800;
  height = parseFloat(height) || size.h || 600;

  // Create nodes map and hierarchy tracking
  const nodes = {};
  const childrenMap = {};
  const lastIndex = generateKeyBetween(null, null);

  // Create the root frame node
  const rootNode = {
    type: TYPES.frame,
    id: createId(),
    name: svgElement.getAttribute('name') || 'SVG Frame',
    description: svgElement.getAttribute('description') || '',
    parentId: null,
    index: lastIndex,
    x: 0,
    y: 0,
    w: width,
    h: height,
    fill: 'transparent',
    transform: '',
    children: [],
  };

  // Initialize children map for root
  childrenMap[rootNode.id] = [];

  // Store gradients, patterns, filters for reference
  const gradients = processGradients(svgElement);
  const patterns = processPatterns(svgElement);
  const images = processImages(svgElement);
  const filters = processFilters(svgElement);

  const defs = {
    gradients,
    patterns,
    images,
    filters,
  };

  const rootBbox = tempSvg.getBoundingClientRect();

  // Improved bounding box calculation that handles transforms properly
  function resolveBbox(element, _parentTransform = null) {
    let bbox = { x: 0, y: 0, width: 0, height: 0 };
    try {
      if (element.getBoundingClientRect) {
        const box = element.getBoundingClientRect();
        bbox = {
          x: box.x,
          y: box.y,
          width: box.width,
          height: box.height,
        };
        bbox.x -= rootBbox.x;
        bbox.y -= rootBbox.y;
      }
    } catch (e) {
      console.warn('Could not calculate element bounds:', e);
    }
    return bbox;
  }

  // 解析 SVG 长度值（支持数字、px、百分比等）
  function parseSvgLength(value, referenceSize) {
    if (value == null || value === '') return 0;
    const str = String(value).trim();
    if (!str) return 0;
    // 处理百分比
    if (str.endsWith('%')) {
      const percent = parseFloat(str);
      return (percent / 100) * referenceSize;
    }
    // 处理 px 单位
    if (str.endsWith('px')) {
      return parseFloat(str);
    }
    // 其他情况直接解析数字
    return parseFloat(str) || 0;
  }

  // 计算元素的原始几何尺寸（不受变换影响）
  function getOriginalGeometry(element) {
    const tagName = element.tagName.toLowerCase();
    let geometry = { x: 0, y: 0, width: 0, height: 0 };

    try {
      if (tagName === 'rect') {
        geometry.x = parseSvgLength(element.getAttribute('x'), width);
        geometry.y = parseSvgLength(element.getAttribute('y'), height);
        geometry.width = parseSvgLength(element.getAttribute('width'), width);
        geometry.height = parseSvgLength(element.getAttribute('height'), height);
      } else if (tagName === 'circle') {
        const cx = parseSvgLength(element.getAttribute('cx'), width);
        const cy = parseSvgLength(element.getAttribute('cy'), height);
        // r 使用 width 作为参考尺寸（也可以用 Math.min(width, height)）
        const r = parseSvgLength(element.getAttribute('r'), Math.min(width, height));
        geometry = { x: cx - r, y: cy - r, width: 2 * r, height: 2 * r };
      } else if (tagName === 'ellipse') {
        const cx = parseSvgLength(element.getAttribute('cx'), width);
        const cy = parseSvgLength(element.getAttribute('cy'), height);
        const rx = parseSvgLength(element.getAttribute('rx'), width);
        const ry = parseSvgLength(element.getAttribute('ry'), height);
        geometry = { x: cx - rx, y: cy - ry, width: 2 * rx, height: 2 * ry };
      } else if (tagName === 'image') {
        geometry.x = parseSvgLength(element.getAttribute('x'), width);
        geometry.y = parseSvgLength(element.getAttribute('y'), height);
        geometry.width = parseSvgLength(element.getAttribute('width'), width);
        geometry.height = parseSvgLength(element.getAttribute('height'), height);
      } else if (tagName === 'line') {
        const x1 = parseSvgLength(element.getAttribute('x1'), width);
        const y1 = parseSvgLength(element.getAttribute('y1'), height);
        const x2 = parseSvgLength(element.getAttribute('x2'), width);
        const y2 = parseSvgLength(element.getAttribute('y2'), height);
        const minX = Math.min(x1, x2);
        const minY = Math.min(y1, y2);
        const maxX = Math.max(x1, x2);
        const maxY = Math.max(y1, y2);
        geometry = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
      } else if (tagName === 'polygon' || tagName === 'polyline') {
        const points = element.getAttribute('points') || '';
        if (points) {
          const coords = points
            .trim()
            .split(/[\s,]+/)
            .map(Number);
          if (coords.length >= 2) {
            const xCoords = [];
            const yCoords = [];
            for (let i = 0; i < coords.length; i += 2) {
              xCoords.push(coords[i]);
              yCoords.push(coords[i + 1]);
            }
            const minX = Math.min(...xCoords);
            const minY = Math.min(...yCoords);
            const maxX = Math.max(...xCoords);
            const maxY = Math.max(...yCoords);
            geometry = { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
          }
        }
      } else {
        // 对于复杂元素，回退到DOM边界框
        return resolveBbox(element);
      }
    } catch (e) {
      console.warn('Could not calculate original geometry:', e);
      return resolveBbox(element);
    }

    return geometry;
  }

  // 计算变换后的节点位置和尺寸，同时计算相对变换
  function calculateNodePositionAndTransform(element, elementTransform, combinedTransform) {
    // 获取原始几何信息
    const originalGeometry = getOriginalGeometry(element);

    if (!combinedTransform) {
      // 没有变换，直接使用原始几何信息
      return {
        x: originalGeometry.x,
        y: originalGeometry.y,
        w: originalGeometry.width,
        h: originalGeometry.height,
        transform: elementTransform ? matrixToString(elementTransform) : '',
      };
    }

    // 获取变换中心点（原始几何形状的中心）
    const centerX = originalGeometry.x + originalGeometry.width / 2;
    const centerY = originalGeometry.y + originalGeometry.height / 2;

    // 分解变换矩阵
    const [a, b, c, d, e, f] = combinedTransform;

    // 计算变换后的中心点
    const transformedCenterX = a * centerX + c * centerY + e;
    const transformedCenterY = b * centerX + d * centerY + f;

    // 计算缩放和旋转
    const scaleX = Math.sqrt(a * a + b * b);
    const scaleY = Math.sqrt(c * c + d * d);
    const rotation = Math.atan2(b, a);

    // 计算节点的新位置（左上角）
    // 以未缩放尺寸为参考来对齐中心点，确保与后续以中心点应用的缩放/旋转一致
    const nodeX = transformedCenterX - originalGeometry.width / 2;
    const nodeY = transformedCenterY - originalGeometry.height / 2;

    // 计算相对变换（去除已应用到位置的部分）
    let nodeTransform = '';

    // 如果存在非平移的变换（缩放、旋转），需要保留这些变换
    const hasScale = Math.abs(scaleX - 1) > 0.001 || Math.abs(scaleY - 1) > 0.001;
    const hasRotation = Math.abs(rotation) > 0.001;

    if (hasScale || hasRotation) {
      // 仅保留缩放/旋转分量，去除平移，由渲染阶段的中心平移处理
      const cosR = Math.cos(rotation);
      const sinR = Math.sin(rotation);
      const relativeMatrix = [scaleX * cosR, scaleX * sinR, -scaleY * sinR, scaleY * cosR, 0, 0];

      nodeTransform = matrixToString(relativeMatrix);
    }

    return {
      x: nodeX,
      y: nodeY,
      w: originalGeometry.width, // 保持原始尺寸，变换在transform中处理
      h: originalGeometry.height, // 保持原始尺寸，变换在transform中处理
      transform: nodeTransform,
    };
  }

  // Process SVG elements
  const processElement = (element, parentId, parentTransform = null) => {
    if (!element || element.nodeType !== 1) return null;

    // Get the parent's children array
    const siblings = childrenMap[parentId] || [];
    const prevSibling = siblings.length > 0 ? siblings[siblings.length - 1] : null;
    const prevIndex = prevSibling ? prevSibling.index : null;

    // Create a new index for this element
    const index = generateKeyBetween(prevIndex, null);

    // Get element attributes
    const tagName = element.tagName.toLowerCase();

    // Get element styles
    const fill = resolveFill(element, defs);
    const stroke = resolveStroke(element);
    const strokeWidth = resolveStrokeWidth(element);
    const opacity = resolveOpacity(element);
    const fillOpacity = parseFloat(getStyleValue(element, 'fill-opacity', '1')) || 1;
    const strokeOpacity = resolveStrokeOpacity(element);
    const strokeLinecap = resolveStrokeLinecap(element);
    const strokeLinejoin = resolveStrokeLinejoin(element);
    const strokeMiterlimit = resolveStrokeMiterlimit(element);
    const strokeDasharray = resolveStrokeDasharray(element);
    const strokeDashoffset = resolveStrokeDashoffset(element);
    const fillRule = resolveFillRule(element);

    const filter = resolveFilter(element, defs);
    const styles = getStyles(element);
    // Get element's own transform
    const elementTransform = getElementTransform(element);

    // Combine parent and element transforms
    const combinedTransform =
      parentTransform && elementTransform
        ? multiplyMatrix(parentTransform, elementTransform)
        : elementTransform || parentTransform;

    // Handle different SVG elements
    if (tagName === 'svg') {
      // Process children of the root SVG
      Array.from(element.children).forEach((child) => {
        const childNode = processElement(child, parentId, combinedTransform);
        if (childNode) {
          // Add to parent's children array
          if (!childrenMap[parentId]) {
            childrenMap[parentId] = [];
          }
          if (Array.isArray(childNode)) {
            childrenMap[parentId].push(...childNode);
          } else {
            childrenMap[parentId].push(childNode);
          }
        }
      });
      return null;
    } else if (tagName === 'g') {
      // Group element - 使用DOM bbox来确定位置
      const groupBbox = resolveBbox(element, combinedTransform);
      const groupId = createId();

      const groupNode = {
        type: TYPES.group,
        id: groupId,
        name: element.getAttribute('id') || 'Group',
        parentId,
        index,
        x: groupBbox.x,
        y: groupBbox.y,
        w: groupBbox.width,
        h: groupBbox.height,
        fill: 'transparent',
        opacity,
        // 子节点的几何信息已基于 combinedTransform 计算，这里避免再次应用变换
        transform: '',
        children: [],
        filter: filter,
      };

      // Initialize children array for this group
      childrenMap[groupId] = [];

      // Process children with the combined transform
      Array.from(element.children).forEach((child) => {
        const childNode = processElement(child, groupId, combinedTransform);

        if (childNode) {
          if (Array.isArray(childNode)) {
            childNode.forEach((child) => {
              // Adjust child coordinates relative to group
              child.x = child.x - groupNode.x;
              child.y = child.y - groupNode.y;
              childrenMap[groupId].push(child);
            });
          } else {
            // Adjust child coordinates relative to group
            childNode.x = childNode.x - groupNode.x;
            childNode.y = childNode.y - groupNode.y;
            childrenMap[groupId].push(childNode);
          }
        }
      });

      nodes[groupId] = groupNode;
      return groupNode;
    } else if (tagName === 'path') {
      // Path element - improved handling for transformed paths
      const d = element.getAttribute('d') || '';

      // Check if this path is a simple line
      const lineNode = createLineFromPath(element, d, nodes, combinedTransform, {
        size: strokeWidth || 1,
        parentId,
        index,
        fill: stroke || '#000000',
        opacity,
        lineCap: strokeLinecap || 'butt',
        lineJoin: strokeLinejoin || 'miter',
        transform: elementTransform ? matrixToString(elementTransform) : '',
        filter: filter,
      });
      if (lineNode) return lineNode;

      // Handle as regular path if not a simple line
      // Get the actual bounding box considering transforms
      const pathBbox = resolvePathToBox(d);

      // If there's a transform, we need to apply it to the path coordinates
      let normalizedPath = d;
      let nodeTransform = '';
      let finalX = pathBbox.x;
      let finalY = pathBbox.y;
      let finalWidth = pathBbox.width;
      let finalHeight = pathBbox.height;

      if (combinedTransform) {
        // Apply transform to path coordinates
        normalizedPath = applyTransformToPath(d, combinedTransform);
        // Recalculate bounding box with transformed coordinates
        const transformedBbox = resolvePathToBox(normalizedPath);
        finalX = transformedBbox.x;
        finalY = transformedBbox.y;
        finalWidth = transformedBbox.width;
        finalHeight = transformedBbox.height;

        // 对于路径，变换已经应用到坐标中，所以不需要额外的transform
        nodeTransform = '';
      } else if (elementTransform) {
        // 如果只有元素自身的变换，保留它
        nodeTransform = matrixToString(elementTransform);
      }

      // Expand bbox to include stroke width (to avoid stroke overflowing the node box)
      const hasVisibleStroke =
        stroke && stroke !== 'none' && stroke !== 'transparent' && (strokeWidth || 0) > 0;
      const strokePad = hasVisibleStroke ? (parseFloat(strokeWidth) || 0) / 2 : 0;

      // Expanded bbox considering stroke
      const expandedX = finalX - strokePad;
      const expandedY = finalY - strokePad;
      const expandedW = Math.max(1, finalWidth + strokePad * 2);
      const expandedH = Math.max(1, finalHeight + strokePad * 2);

      // Normalize path to use expanded bbox origin
      normalizedPath = resolveNormalizedPath(normalizedPath, {
        x: expandedX,
        y: expandedY,
        width: expandedW,
        height: expandedH,
      });

      const nodeId = createId();
      const pathNode = {
        type: TYPES.path,
        id: nodeId,
        name: element.getAttribute('id') || 'Path',
        parentId,
        index,
        x: expandedX,
        y: expandedY,
        w: expandedW,
        h: expandedH,
        path: normalizedPath,
        fill: fill,
        stroke,
        strokeWidth,
        opacity,
        transform: nodeTransform,
        strokeLinecap,
        strokeLinejoin,
        strokeMiterlimit,
        strokeDasharray,
        strokeDashoffset,
        fillRule,
        fillOpacity,
        strokeOpacity,
        filter: filter,
      };

      nodes[nodeId] = pathNode;
      return pathNode;
    } else if (['rect', 'circle', 'ellipse', 'polygon'].includes(tagName)) {
      // Shape elements - 使用新的位置和变换计算方法
      const positionAndTransform = calculateNodePositionAndTransform(
        element,
        elementTransform,
        combinedTransform,
      );

      let shape = SHAPES.rect;
      let points = '';
      let radius = 0;

      if (isImageFilled(element, defs)) {
        // Extract the image definition from the fill
        const imageFill = isImageFilled(element, defs);

        // Create an image node instead of a shape
        const nodeId = createId();
        const imageNode = {
          type: TYPES.image,
          id: nodeId,
          name: element.getAttribute('id') || 'Image',
          parentId,
          index,
          meta: {},
          x: positionAndTransform.x,
          y: positionAndTransform.y,
          w: positionAndTransform.w,
          h: positionAndTransform.h,
          src: imageFill.href,
          opacity: opacity || imageFill.opacity || 1,
          preserveAspectRatio: imageFill.preserveAspectRatio || 'xMidYMid meet',
          transform: positionAndTransform.transform,
          filter: filter,
        };

        nodes[nodeId] = imageNode;
        return imageNode;
      }

      // Set shape-specific properties
      if (tagName === 'rect') {
        radius = resolveRadius(element, styles);
      } else if (tagName === 'circle') {
        shape = SHAPES.ellipse;
        radius = parseSvgLength(element.getAttribute('r'), Math.min(width, height));
      } else if (tagName === 'ellipse') {
        shape = SHAPES.ellipse;
      } else if (tagName === 'polygon') {
        shape = SHAPES.polygon;
        points = element.getAttribute('points') || '';
      }

      const nodeId = createId();
      const shapeNode = {
        type: TYPES.shape,
        shape: shape,
        id: nodeId,
        name: element.getAttribute('id') || tagName,
        parentId,
        index,
        x: positionAndTransform.x,
        y: positionAndTransform.y,
        w: positionAndTransform.w,
        h: positionAndTransform.h,
        fill: fill,
        stroke,
        strokeWidth,
        radius,
        opacity,
        transform: positionAndTransform.transform,
        points,
        strokeLinecap,
        strokeLinejoin,
        strokeMiterlimit,
        strokeDasharray,
        strokeDashoffset,
        fillRule,
        fillOpacity,
        strokeOpacity,
        filter: filter,
      };

      nodes[nodeId] = shapeNode;
      return shapeNode;
    } else if (tagName === 'polyline') {
      // Handle polyline separately: open polyline -> TYPES.line; closed polyline or filled -> TYPES.path
      const rawPoints = (element.getAttribute('points') || '').trim();
      if (!rawPoints) return null;

      // Parse points into [{x,y}]
      const values = rawPoints
        .split(/[\s,]+/)
        .map(Number)
        .filter((v) => !isNaN(v));
      const points = [];
      for (let i = 0; i + 1 < values.length; i += 2) {
        points.push({ x: values[i], y: values[i + 1] });
      }
      if (points.length < 2) return null;

      // Determine closedness
      const EPS = 1e-6;
      const first = points[0];
      const last = points[points.length - 1];
      const explicitlyClosed = Math.abs(first.x - last.x) < EPS && Math.abs(first.y - last.y) < EPS;
      const hasFill = fill && fill !== 'none' && fill !== 'transparent';

      // Prepare transform handling
      let nodeTransform = '';
      let transformedPoints = points;
      if (combinedTransform) {
        const [a, b, c, d, e, f] = combinedTransform;
        transformedPoints = points.map((p) => ({
          x: a * p.x + c * p.y + e,
          y: b * p.x + d * p.y + f,
        }));
        nodeTransform = ''; // transform baked into coordinates
      } else if (elementTransform) {
        nodeTransform = matrixToString(elementTransform);
      }

      // Compute bbox
      const xCoords = transformedPoints.map((p) => p.x);
      const yCoords = transformedPoints.map((p) => p.y);
      const minX = Math.min(...xCoords);
      const minY = Math.min(...yCoords);
      const maxX = Math.max(...xCoords);
      const maxY = Math.max(...yCoords);
      const width = Math.max(maxX - minX, 8);
      const height = Math.max(maxY - minY, 8);

      if (explicitlyClosed || hasFill) {
        // Closed scenario -> create a path with Z, normalized to (minX,minY)
        const dPath =
          transformedPoints
            .map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x - minX} ${p.y - minY}`)
            .join(' ') + ' Z';

        const nodeId = createId();
        const pathNode = {
          type: TYPES.path,
          id: nodeId,
          name: element.getAttribute('id') || 'PolylinePath',
          parentId,
          index,
          x: minX,
          y: minY,
          w: width,
          h: height,
          path: dPath,
          fill: fill,
          stroke,
          strokeWidth,
          opacity,
          transform: nodeTransform,
          strokeLinecap,
          strokeLinejoin,
          strokeMiterlimit,
          strokeDasharray,
          strokeDashoffset,
          fillRule,
          fillOpacity,
          strokeOpacity,
          filter: filter,
        };
        nodes[nodeId] = pathNode;
        return pathNode;
      } else {
        // Open scenario -> create a line with multiple points (polyline)
        const dashArray = resolveLineStrokeDasharray(element);

        // Convert to relative (0..1) points within bbox
        const relativePoints = transformedPoints.map((p) => {
          const rx = width > 1 ? (p.x - minX) / width : 0;
          const ry = height > 1 ? (p.y - minY) / height : 0;
          return `${rx},${ry}`;
        });

        const nodeId = createId();
        const startMarker = detectArrowMarker(element, 'marker-start');
        const endMarker = detectArrowMarker(element, 'marker-end');

        const lineNode = {
          type: TYPES.line,
          id: nodeId,
          name: element.getAttribute('id') || 'Polyline',
          parentId,
          index,
          x: minX,
          y: minY,
          w: width,
          h: height,
          points: relativePoints.join(' '),
          size: strokeWidth || 1,
          strokeStyle: dashArray ? 'dashed' : 'solid',
          dashArray,
          fill: stroke || '#000000',
          opacity,
          lineCap: strokeLinecap || 'butt',
          lineJoin: strokeLinejoin || 'miter',
          startMarker,
          endMarker,
          transform: nodeTransform,
          filter: filter,
        };
        nodes[nodeId] = lineNode;
        return lineNode;
      }
    } else if (tagName === 'text') {
      const transformedBbox = resolveBbox(element, combinedTransform);
      // 文本的 x/y 基于真实渲染后的 bbox，这里为避免平移重复，仅保留缩放/旋转成分
      const rawTransform = combinedTransform || elementTransform || null;
      const relativeTransform = rawTransform
        ? computeRelativeTransform(rawTransform, transformedBbox, true)
        : '';
      const context = {
        bbox: transformedBbox,
        fill,
        stroke,
        strokeWidth,
        opacity,
        fillOpacity,
        strokeOpacity,
        transform: relativeTransform,
        filter,
      };
      return processTextElement(element, parentId, index, context);
    } else if (tagName === 'image') {
      // Image element - 使用新的位置和变换计算方法
      const positionAndTransform = calculateNodePositionAndTransform(
        element,
        elementTransform,
        combinedTransform,
      );
      const href = element.getAttribute('href') || element.getAttribute('xlink:href') || '';
      const preserveAspectRatio = element.getAttribute('preserveAspectRatio') || 'xMidYMid meet';

      const nodeId = createId();
      const imageNode = {
        type: TYPES.image,
        id: nodeId,
        name: element.getAttribute('id') || 'Image',
        parentId,
        index,
        x: positionAndTransform.x,
        y: positionAndTransform.y,
        w: positionAndTransform.w,
        h: positionAndTransform.h,
        src: href,
        opacity,
        preserveAspectRatio,
        transform: positionAndTransform.transform,
        filter: filter,
      };

      nodes[nodeId] = imageNode;
      return imageNode;
    } else if (tagName === 'line') {
      // Line element
      let x1 = parseSvgLength(element.getAttribute('x1'), width);
      let y1 = parseSvgLength(element.getAttribute('y1'), height);
      let x2 = parseSvgLength(element.getAttribute('x2'), width);
      let y2 = parseSvgLength(element.getAttribute('y2'), height);

      let nodeTransform = '';

      // Apply transform if present
      if (combinedTransform) {
        const [a, b, c, d, e, f] = combinedTransform;
        // Transform start point
        const transformedX1 = a * x1 + c * y1 + e;
        const transformedY1 = b * x1 + d * y1 + f;
        // Transform end point
        const transformedX2 = a * x2 + c * y2 + e;
        const transformedY2 = b * x2 + d * y2 + f;

        x1 = transformedX1;
        y1 = transformedY1;
        x2 = transformedX2;
        y2 = transformedY2;

        // 线的变换已经应用到坐标中
        nodeTransform = '';
      } else if (elementTransform) {
        // 如果只有元素自身的变换，保留它
        nodeTransform = matrixToString(elementTransform);
      }

      // Calculate bounding box for the line
      const minX = Math.min(x1, x2);
      const minY = Math.min(y1, y2);
      const maxX = Math.max(x1, x2);
      const maxY = Math.max(y1, y2);

      const lineWidth = Math.max(maxX - minX, 8);
      const lineHeight = Math.max(maxY - minY, 8);

      // Convert absolute coordinates to relative coordinates (0-1 range)
      const relativeX1 = lineWidth > 1 ? (x1 - minX) / lineWidth : 0;
      const relativeY1 = lineHeight > 1 ? (y1 - minY) / lineHeight : 0;
      const relativeX2 = lineWidth > 1 ? (x2 - minX) / lineWidth : 1;
      const relativeY2 = lineHeight > 1 ? (y2 - minY) / lineHeight : 1;

      const nodeId = createId();
      const dashArray = resolveLineStrokeDasharray(element);

      const lineNode = {
        type: TYPES.line,
        id: nodeId,
        name: element.getAttribute('id') || 'Line',
        parentId,
        index,
        x: minX,
        y: minY,
        w: lineWidth,
        h: lineHeight,
        points: `${relativeX1},${relativeY1} ${relativeX2},${relativeY2}`,
        size: strokeWidth || 1,
        strokeStyle: dashArray ? 'dashed' : 'solid',
        dashArray: dashArray,
        fill: stroke || '#000000',
        opacity,
        lineCap: strokeLinecap || 'butt',
        lineJoin: strokeLinejoin || 'miter',
        transform: nodeTransform,
        filter: filter,
      };
      nodes[nodeId] = lineNode;
      return lineNode;
    }

    return null;
  };

  // Process text elements
  const processTextElement = (element, parentId, index, context) => {
    const { fill, stroke, strokeWidth, opacity, transform, filter } = context;
    // Check if text has tspan children
    const tspans = element.querySelectorAll('tspan');

    if (tspans.length > 0) {
      // Process text with mixed content (text + tspans)
      let currentIndex = index;
      let lastNode = null;
      const textNodes = [];

      const parentFontSize = resolveFontSize(element);
      const bbox = context.bbox;
      const parentXAttr = element.hasAttribute('x')
        ? parseLength(element.getAttribute('x'), parentFontSize)
        : null;
      const parentYAttr = element.hasAttribute('y')
        ? parseLength(element.getAttribute('y'), parentFontSize)
        : null;
      const baseXCoord = bbox.x;
      const baseYCoord = bbox.y;
      let currentX = baseXCoord;
      let currentY = baseYCoord;

      // First, handle any direct text content before the first tspan
      let textBeforeTspan = '';
      let node = element.firstChild;
      while (node) {
        if (node.nodeType === 3 && node.textContent.trim()) {
          // Text node
          textBeforeTspan += node.textContent;
        } else if (node.nodeType === 1 && node.tagName.toLowerCase() === 'tspan') {
          break; // Stop at first tspan
        }
        node = node.nextSibling;
      }

      // Create a text node for content before tspans if it exists
      if (textBeforeTspan.trim()) {
        const fontSize = resolveFontSize(element);
        const fontFamily = resolveFontFamily(element);

        const mainTextNodeId = createId();
        const mainTextNode = {
          type: TYPES.text,
          id: mainTextNodeId,
          name: element.getAttribute('id') || 'Text_main',
          parentId,
          index: currentIndex,
          x: bbox.x,
          y: bbox.y,
          w: textBeforeTspan.length * fontSize * 0.6,
          h: fontSize * 1.3,
          padding: 1,
          width: LayoutSizeType.hug,
          text: textBeforeTspan.trim(),
          alignItems: 'center',
          fontSize,
          fontFamily,
          fontWeight: resolveFontWeight(element),
          fontStyle: resolveFontStyle(element),
          textDecoration: resolveTextDecoration(element),
          textAnchor: resolveTextAnchor(element),
          fill: fill,
          stroke,
          strokeWidth,
          opacity,
          transform,
          filter: filter,
        };

        nodes[mainTextNodeId] = mainTextNode;
        textNodes.push(mainTextNode);
        lastNode = mainTextNode;
        currentIndex = generateKeyBetween(currentIndex, null);

        // Keep baseline anchored for following tspans
        currentX = baseXCoord;
        currentY = baseYCoord;
      }

      // Process each tspan as a separate text element
      for (let i = 0; i < tspans.length; i++) {
        const tspan = tspans[i];

        // Extract or inherit text properties
        const tspanFontSize = resolveFontSize(tspan);
        const fontFamily = resolveFontFamily(tspan);
        const tspanFill = resolveFill(tspan, defs) || fill; // Inherit from parent if not specified
        const tspanFontWeight = resolveFontWeight(tspan) || resolveFontWeight(element);
        const tspanFontStyle = resolveFontStyle(tspan) || resolveFontStyle(element);
        const tspanTextDecoration = resolveTextDecoration(tspan) || resolveTextDecoration(element);

        const textContent = (tspan.textContent || '').trim();
        const tspanNodeId = createId();

        // Create index for this tspan
        if (lastNode) {
          currentIndex = generateKeyBetween(lastNode.index, null);
        }

        const tspanBBox = resolveBbox(tspan);

        // Calculate position honoring x/dx/y/dy relative to parent <text>
        let xPos = currentX;
        let yPos = currentY;

        if (tspan.hasAttribute('x')) {
          const tspanXAttr = parseLength(tspan.getAttribute('x'), tspanFontSize);
          xPos = parentXAttr != null ? baseXCoord + (tspanXAttr - parentXAttr) : tspanXAttr;
        }
        if (tspan.hasAttribute('dx')) {
          xPos += parseLength(tspan.getAttribute('dx'), tspanFontSize);
        }
        if (tspan.hasAttribute('y')) {
          const tspanYAttr = parseLength(tspan.getAttribute('y'), tspanFontSize);
          yPos = parentYAttr != null ? baseYCoord + (tspanYAttr - parentYAttr) : tspanYAttr;
        }
        if (tspan.hasAttribute('dy')) {
          yPos += parseLength(tspan.getAttribute('dy'), tspanFontSize);
        }

        const tspanNode = {
          type: TYPES.text,
          id: tspanNodeId,
          name: tspan.getAttribute('id') ? `${tspan.getAttribute('id')}` : `Text_tspan${i}`,
          parentId,
          index: currentIndex,
          x: xPos,
          y: yPos,
          w: Math.max(tspanBBox.width, textContent.length * tspanFontSize * 0.6), // Estimate width
          h: tspanBBox.height || tspanFontSize * 1.3,
          alignItems: 'center',
          width: LayoutSizeType.hug,
          text: textContent,
          fontSize: tspanFontSize,
          fontFamily: fontFamily,
          fontWeight: tspanFontWeight,
          fontStyle: tspanFontStyle,
          textDecoration: tspanTextDecoration,
          textAnchor: resolveTextAnchor(element),
          fill: tspanFill,
          stroke: getStyleValue(tspan, 'stroke') || stroke,
          strokeWidth: getStyleValue(tspan, 'stroke-width') || strokeWidth,
          opacity: getStyleValue(tspan, 'opacity') || opacity,
          filter: getStyleValue(tspan, 'filter') || filter,
          transform: transform,
        };

        nodes[tspanNodeId] = tspanNode;
        lastNode = tspanNode;
        textNodes.push(tspanNode);

        // Update the running cursor for subsequent tspans
        currentX = xPos;
        currentY = yPos;
      }

      return textNodes;
    } else {
      const bbox = context.bbox;
      const fontSize = resolveFontSize(element);
      const fontFamily = resolveFontFamily(element);
      const textContent = element.textContent || '';
      const textAnchor = resolveTextAnchor(element);
      const fontWeight = resolveFontWeight(element);
      const fontStyle = resolveFontStyle(element);
      const textDecoration = resolveTextDecoration(element);

      const nodeId = createId();
      const textNode = {
        type: TYPES.text,
        id: nodeId,
        name: element.getAttribute('id') || 'Text',
        parentId,
        index,
        x: bbox.x,
        y: bbox.y,
        w: bbox.width || textContent.length * fontSize * 0.6, // Fallback to estimate if bbox is 0
        h: bbox.height || fontSize * 1.3, // Fallback to estimate if bbox is 0
        width: LayoutSizeType.hug,
        padding: 1,
        text: textContent.trim(),
        fontSize,
        fontFamily,
        fontWeight,
        fontStyle,
        textDecoration,
        textAnchor,
        alignItems: 'center',
        fill: fill,
        stroke,
        strokeWidth,
        opacity,
        transform,
        filter: filter,
      };

      nodes[nodeId] = textNode;
      return textNode;
    }
  };

  // Start processing from the root SVG element
  Array.from(svgElement.children).forEach((child) => {
    const childNode = processElement(child, rootNode.id);
    if (childNode) {
      if (Array.isArray(childNode)) {
        childrenMap[rootNode.id].push(...childNode);
      } else {
        childrenMap[rootNode.id].push(childNode);
      }
    }
  });

  // Build the hierarchy by assigning children to their parents
  Object.keys(childrenMap).forEach((parentId) => {
    if (nodes[parentId]) {
      nodes[parentId].children = childrenMap[parentId];
    }
  });

  // Assign children to root node
  rootNode.children = childrenMap[rootNode.id] || [];

  // Clean up the temporary element
  document.body.removeChild(tempEl);

  // Add root node to nodes map
  nodes[rootNode.id] = rootNode;

  // Apply the optimization
  removeTransparentShapeNodes(nodes);

  markEmptyGroups(nodes);

  return { root: rootNode, nodes };
}

// Mark empty groups that have no rendering characteristics
function markEmptyGroups(nodes) {
  Object.values(nodes).forEach((node) => {
    if (node.type === TYPES.group) {
      // Check if the group has any rendering characteristics
      const hasRenderingCharacteristics =
        // Has visible fill
        (node.fill && node.fill !== 'none' && node.fill !== 'transparent') ||
        // Has visible stroke
        (node.stroke &&
          node.stroke !== 'none' &&
          node.stroke !== 'transparent' &&
          node.strokeWidth);

      // If group has no rendering characteristics, mark it as empty
      if (!hasRenderingCharacteristics) {
        node._empty = true;
      }
    }
  });

  return nodes;
}

// Optimize by removing unnecessary transparent shape nodes
function removeTransparentShapeNodes(nodes) {
  const nodesToRemove = new Set();

  Object.values(nodes).forEach((node) => {
    if (node.type === TYPES.shape) {
      // Check if the shape is effectively transparent
      const isTransparent =
        // No fill or transparent fill
        ((!node.fill || node.fill === 'none' || node.fill === 'transparent') &&
          // No stroke or transparent stroke
          (!node.stroke ||
            node.stroke === 'none' ||
            node.stroke === 'transparent' ||
            !node.strokeWidth)) ||
        // Opacity is 0
        node.opacity === 0 ||
        // Fill opacity is 0 and no stroke
        (node.fillOpacity === 0 &&
          (!node.stroke ||
            node.stroke === 'none' ||
            node.stroke === 'transparent' ||
            !node.strokeWidth)) ||
        // Stroke opacity is 0 and no fill
        (node.strokeOpacity === 0 &&
          (!node.fill || node.fill === 'none' || node.fill === 'transparent'));

      if (isTransparent) {
        nodesToRemove.add(node.id);
      }
    }
  });

  // Remove the transparent nodes and update parent-child relationships
  nodesToRemove.forEach((nodeId) => {
    const node = nodes[nodeId];
    if (node && node.parentId && nodes[node.parentId]) {
      const parent = nodes[node.parentId];
      if (parent.children) {
        parent.children = parent.children.filter((child) => child.id !== nodeId);
      }
    }
    delete nodes[nodeId];
  });

  return nodes;
}

// Helper function to compute relative transform for a node in its new coordinate system
function computeRelativeTransform(elementTransform, bbox, appliedTransform) {
  if (!elementTransform) return '';

  // If we already applied the transform to coordinates, we need to calculate
  // what transform should be applied relative to the new position
  if (appliedTransform) {
    // The transform was already applied to positioning, so we need identity or no transform
    // However, if there are scale/rotation components that affect rendering, we should preserve those
    const [a, b, c, d] = elementTransform;

    // Extract scale and rotation components
    const scaleX = Math.sqrt(a * a + b * b);
    const scaleY = Math.sqrt(c * c + d * d);
    const rotation = Math.atan2(b, a);

    // If there's only translation, return empty transform since it's already applied
    if (
      Math.abs(scaleX - 1) < 0.001 &&
      Math.abs(scaleY - 1) < 0.001 &&
      Math.abs(rotation) < 0.001
    ) {
      return '';
    }

    // For non-trivial transforms, create a transform without translation
    const cosR = Math.cos(rotation);
    const sinR = Math.sin(rotation);
    const relativeMatrix = [scaleX * cosR, scaleX * sinR, -scaleY * sinR, scaleY * cosR, 0, 0];

    return matrixToString(relativeMatrix);
  }

  return matrixToString(elementTransform);
}

// Parse SVG length values (supports number, px, em)
function parseLength(value, fontSize = 16) {
  if (value == null) return 0;
  if (typeof value === 'number') return value;
  const str = String(value).trim();
  if (!str) return 0;
  if (str.endsWith('em')) return parseFloat(str) * fontSize;
  if (str.endsWith('px')) return parseFloat(str);
  return parseFloat(str) || 0;
}

function createLineFromPath(element, d, nodes, combinedTransform, patch) {
  if (isSimpleLine(d)) {
    const coords = extractLineCoords(d);
    if (coords) {
      let { x1, y1, x2, y2, isPolyline } = coords;

      let nodeTransform = '';

      // Apply transform if present
      if (combinedTransform) {
        const [a, b, c, d, e, f] = combinedTransform;
        // Transform start point
        const transformedX1 = a * x1 + c * y1 + e;
        const transformedY1 = b * x1 + d * y1 + f;
        // Transform end point
        const transformedX2 = a * x2 + c * y2 + e;
        const transformedY2 = b * x2 + d * y2 + f;

        x1 = transformedX1;
        y1 = transformedY1;
        x2 = transformedX2;
        y2 = transformedY2;

        // 变换已经应用到坐标中，所以不需要额外的transform
        nodeTransform = '';
      } else {
        // 如果没有combinedTransform但patch中有transform，使用它
        nodeTransform = patch.transform || '';
      }

      const nodeId = createId();
      const dashArray = resolveLineStrokeDasharray(element);

      // Parse marker attributes
      const startMarker = detectArrowMarker(element, 'marker-start');
      const endMarker = detectArrowMarker(element, 'marker-end');

      if (isPolyline) {
        // For polylines (multi-segment paths), use the polyline points
        const points = coords.points || [];

        // Apply transform if present to all points
        let transformedPoints = points;
        if (combinedTransform) {
          const [a, b, c, d, e, f] = combinedTransform;
          transformedPoints = points.map((point) => ({
            x: a * point.x + c * point.y + e,
            y: b * point.x + d * point.y + f,
          }));
        }

        // Calculate bounding box for all points
        const xCoords = transformedPoints.map((p) => p.x);
        const yCoords = transformedPoints.map((p) => p.y);
        const minX = Math.min(...xCoords);
        const minY = Math.min(...yCoords);
        const maxX = Math.max(...xCoords);
        const maxY = Math.max(...yCoords);

        const lineWidth = Math.max(maxX - minX, 8);
        const lineHeight = Math.max(maxY - minY, 8);

        // Convert absolute coordinates to relative coordinates (0-1 range)
        const relativePoints = transformedPoints.map((point) => {
          const relativeX = lineWidth > 1 ? (point.x - minX) / lineWidth : 0;
          const relativeY = lineHeight > 1 ? (point.y - minY) / lineHeight : 0;
          return `${relativeX},${relativeY}`;
        });

        const lineNode = {
          type: TYPES.line,
          id: nodeId,
          name: element.getAttribute('id') || 'Polyline',
          x: minX,
          y: minY,
          w: lineWidth,
          h: lineHeight,
          points: relativePoints.join(' '),
          dashArray,
          strokeStyle: dashArray ? 'dashed' : 'solid',
          startMarker,
          endMarker,
          transform: nodeTransform,
          ...patch,
        };
        nodes[nodeId] = lineNode;
        return lineNode;
      } else {
        // For simple lines (2 points)
        // Calculate bounding box for the line
        const minX = Math.min(x1, x2);
        const minY = Math.min(y1, y2);
        const maxX = Math.max(x1, x2);
        const maxY = Math.max(y1, y2);

        const lineWidth = Math.max(maxX - minX, 8);
        const lineHeight = Math.max(maxY - minY, 8);

        // Convert absolute coordinates to relative coordinates (0-1 range)
        const relativeX1 = lineWidth > 1 ? (x1 - minX) / lineWidth : 0;
        const relativeY1 = lineHeight > 1 ? (y1 - minY) / lineHeight : 0;
        const relativeX2 = lineWidth > 1 ? (x2 - minX) / lineWidth : 1;
        const relativeY2 = lineHeight > 1 ? (y2 - minY) / lineHeight : 1;

        const lineNode = {
          type: TYPES.line,
          id: nodeId,
          name: element.getAttribute('id') || 'Line',
          x: minX,
          y: minY,
          w: lineWidth,
          h: lineHeight,
          points: `${relativeX1},${relativeY1} ${relativeX2},${relativeY2}`,
          dashArray,
          strokeStyle: dashArray ? 'dashed' : 'solid',
          startMarker,
          endMarker,
          transform: nodeTransform,
          ...patch,
        };
        nodes[nodeId] = lineNode;
        return lineNode;
      }
    }
  }
}
